import i18n from 'i18n-js';

import type { EditorTable } from './context';
import {
    EditorArea,
    isPointInPolygon,
    Point,
    SizeMeters,
    tableFootprintMeters,
    TableShapeSizes,
} from './mapGeometry';
import {
    MapOpening,
    OPENING_BAR_LENGTH_METERS,
    OPENING_BAR_WIDTH_METERS,
    openingOutlinePoint,
} from './openings';
import { cornersToArea } from './roomOutline';

export type MapFeatureKind = 'Pillar' | 'Stairs' | 'Wall' | 'BarCounter' | 'Furniture' | 'Custom';
export type PresetFeatureKind = Exclude<MapFeatureKind, 'Custom'>;

/**
 * `position`: centre, normalized 0..1 against the map's global rectangle. `width`/`height`: metres
 * before rotation. `points` (Custom only): 0..1 within that bbox.
 */
export interface MapFeature {
    id: string;
    kind: MapFeatureKind;
    label: string | null;
    position: Point;
    width: number;
    height: number;
    rotation: number;
    isRound: boolean;
    points: Point[] | null;
}

/** TablesMapManager.MaxMapFeatures / MinMapFeatureSizeMeters / MaxGlobalMapSizeMeters / MaxMapFeatureLabelLength. */
export const MAX_FEATURES = 100;
export const FEATURE_SIZE_MIN = 0.1;
export const FEATURE_SIZE_MAX = 100;
export const FEATURE_LABEL_MAX_LENGTH = 50;

export const PRESET_FEATURE_KINDS: PresetFeatureKind[] = [
    'Wall',
    'Pillar',
    'Stairs',
    'BarCounter',
    'Furniture',
];

const PRESET_DEFAULTS: Record<PresetFeatureKind, SizeMeters & { isRound: boolean }> = {
    Pillar: { width: 0.4, height: 0.4, isRound: true },
    Stairs: { width: 1.2, height: 2.4, isRound: false },
    Wall: { width: 3, height: 0.15, isRound: false },
    BarCounter: { width: 3, height: 0.8, isRound: false },
    Furniture: { width: 1.5, height: 0.8, isRound: false },
};

export const FEATURE_KIND_LABEL_KEY: Record<MapFeatureKind, string> = {
    Pillar: 'tableMaps.wizard.features.kindPillar',
    Stairs: 'tableMaps.wizard.features.kindStairs',
    Wall: 'tableMaps.wizard.features.kindWall',
    BarCounter: 'tableMaps.wizard.features.kindBarCounter',
    Furniture: 'tableMaps.wizard.features.kindFurniture',
    Custom: 'tableMaps.wizard.features.kindCustom',
};

const ELLIPSE_SEGMENTS = 32;
const TOUCH_EPSILON_METERS = 1e-6;

function newFeatureId(): string {
    return `feature-${Date.now()}-${Math.random().toString(36).slice(2, 7)}`;
}

export function presetFeature(kind: PresetFeatureKind, position: Point): MapFeature {
    const { width, height, isRound } = PRESET_DEFAULTS[kind];
    return {
        id: newFeatureId(),
        kind,
        label: null,
        position,
        width,
        height,
        rotation: 0,
        isRound,
        points: null,
    };
}

export function customFeatureFromCorners(corners: Point[], mapSize: SizeMeters): MapFeature {
    const { offset, size, points } = cornersToArea(corners);
    return {
        id: newFeatureId(),
        kind: 'Custom',
        label: null,
        position: {
            x: (offset.x + size.width / 2) / mapSize.width,
            y: (offset.y + size.height / 2) / mapSize.height,
        },
        width: size.width,
        height: size.height,
        rotation: 0,
        isRound: false,
        points,
    };
}

export function featureName(feature: MapFeature): string {
    return feature.label?.trim() || i18n.t(FEATURE_KIND_LABEL_KEY[feature.kind]);
}

export function featureDimensions(feature: MapFeature): string {
    if (feature.kind === 'Pillar' && feature.isRound && feature.width === feature.height) {
        return `⌀${feature.width} m`;
    }
    return `${feature.width} × ${feature.height} m`;
}

/** `p` turned `degrees` clockwise (SVG axes) about the origin. */
export function rotate(p: Point, degrees: number): Point {
    const rad = (degrees * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);
    return { x: p.x * cos - p.y * sin, y: p.x * sin + p.y * cos };
}

function footprintPolygon(
    center: Point,
    size: SizeMeters,
    rotation: number,
    shape: 'rect' | 'ellipse' | Point[]
): Point[] {
    const hw = size.width / 2;
    const hh = size.height / 2;
    let local: Point[];
    if (shape === 'rect') {
        local = [
            { x: -hw, y: -hh },
            { x: hw, y: -hh },
            { x: hw, y: hh },
            { x: -hw, y: hh },
        ];
    } else if (shape === 'ellipse') {
        local = Array.from({ length: ELLIPSE_SEGMENTS }, (_, i) => {
            const angle = (2 * Math.PI * i) / ELLIPSE_SEGMENTS;
            return { x: hw * Math.cos(angle), y: hh * Math.sin(angle) };
        });
    } else {
        local = shape.map((p) => ({ x: (p.x - 0.5) * size.width, y: (p.y - 0.5) * size.height }));
    }
    return local.map((p) => {
        const r = rotate(p, rotation);
        return { x: center.x + r.x, y: center.y + r.y };
    });
}

function cross(a: Point, b: Point, c: Point): number {
    return (b.x - a.x) * (c.y - a.y) - (b.y - a.y) * (c.x - a.x);
}

function segmentsCross(a1: Point, a2: Point, b1: Point, b2: Point): boolean {
    return cross(a1, a2, b1) * cross(a1, a2, b2) < 0 && cross(b1, b2, a1) * cross(b1, b2, a2) < 0;
}

function polygonsOverlap(a: Point[], b: Point[]): boolean {
    const edgesCross = a.some((a1, i) =>
        b.some((b1, j) => segmentsCross(a1, a[(i + 1) % a.length], b1, b[(j + 1) % b.length]))
    );
    return (
        edgesCross || a.some((p) => isPointInPolygon(p, b)) || b.some((p) => isPointInPolygon(p, a))
    );
}

function featurePolygon(feature: MapFeature, mapSize: SizeMeters): Point[] {
    const center = {
        x: feature.position.x * mapSize.width,
        y: feature.position.y * mapSize.height,
    };
    const shape =
        feature.kind === 'Custom' && feature.points
            ? feature.points
            : feature.kind === 'Pillar' && feature.isRound
            ? 'ellipse'
            : 'rect';
    return footprintPolygon(center, feature, feature.rotation, shape);
}

/** The bar is a doorway of fixed size in metres, so the result does not depend on the map's scale. */
export function openingsOverlappingFeature(
    feature: MapFeature,
    openings: MapOpening[],
    mapSize: SizeMeters,
    area: EditorArea
): MapOpening[] {
    const barSize = { width: OPENING_BAR_LENGTH_METERS, height: OPENING_BAR_WIDTH_METERS };
    const footprint = featurePolygon(feature, mapSize);
    return openings.filter((opening) => {
        const { point, tangent } = openingOutlinePoint(opening, mapSize, area);
        const rotation = (Math.atan2(tangent.y, tangent.x) * 180) / Math.PI;
        return polygonsOverlap(footprintPolygon(point, barSize, rotation, 'rect'), footprint);
    });
}

export function tablesOverlappingFeatures(
    tables: EditorTable[],
    features: MapFeature[],
    mapSize: SizeMeters,
    tableDefaults: TableShapeSizes
): Set<string> {
    const overlapping = tables.filter((t) =>
        features.some((f) => tableOverlapsFeature(t, f, mapSize, tableDefaults))
    );
    return new Set(overlapping.map((t) => t.id));
}

export function overlappingTableIds(
    tables: EditorTable[],
    features: MapFeature[],
    mapSize: SizeMeters,
    tableDefaults: TableShapeSizes
): Set<string> {
    const ids = tablesOverlappingFeatures(tables, features, mapSize, tableDefaults);
    tables.forEach((a, i) =>
        tables.slice(i + 1).forEach((b) => {
            if (tablesOverlap(a, b, mapSize, tableDefaults)) {
                ids.add(a.id);
                ids.add(b.id);
            }
        })
    );
    return ids;
}

/** Pulled in by a micrometre, so a table that only touches something never counts as overlapping. */
function tablePolygon(
    table: EditorTable,
    mapSize: SizeMeters,
    tableDefaults: TableShapeSizes
): Point[] {
    const size = tableFootprintMeters(table.shape, table.width, table.height, tableDefaults);
    const center = { x: table.x * mapSize.width, y: table.y * mapSize.height };
    const inset = 2 * TOUCH_EPSILON_METERS;
    if (table.shape === 'round') {
        const diameter = Math.min(size.width, size.height) - inset;
        return footprintPolygon(center, { width: diameter, height: diameter }, 0, 'ellipse');
    }
    const insetSize = { width: size.width - inset, height: size.height - inset };
    return footprintPolygon(center, insetSize, table.rotation, 'rect');
}

export function tableOverlapsFeature(
    table: EditorTable,
    feature: MapFeature,
    mapSize: SizeMeters,
    tableDefaults: TableShapeSizes
): boolean {
    return polygonsOverlap(
        tablePolygon(table, mapSize, tableDefaults),
        featurePolygon(feature, mapSize)
    );
}

export function tablesOverlap(
    a: EditorTable,
    b: EditorTable,
    mapSize: SizeMeters,
    tableDefaults: TableShapeSizes
): boolean {
    return polygonsOverlap(
        tablePolygon(a, mapSize, tableDefaults),
        tablePolygon(b, mapSize, tableDefaults)
    );
}
